<template>
  <v-container fluid class="py-0 px-2">
    <v-text-field
      v-model="search"
      background-color="colorSearchSettings"
      clearable
      :append-icon="mdiMagnify"
      :label="$t('label.search')"
      hide-details
    />
    <div style="min-height: 1px"></div>
    <v-data-table
      :headers="headers"
      :items="tableData"
      item-key="id"
      :height="$store.state.windowSize.y - 210"
      :items-per-page="20"
      :footer-props="{
        'items-per-page-options': [10, 20, 40, 60, 80, 100],
      }"
      :search="search"
      fixed-header
      must-sort
      :loading="loaderStatus"
      class="tableCursor"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.plate }}</td>
          <td>{{ item.state }}</td>
          <td>{{ item.type }}</td>
          <td>{{ item.color }}</td>
          <td
            v-if="$store.state.auth.userSettings.accCars.update"
            class="px-2 tableSelectCursor"
            @click="((modalEdit = true), (car = { ...item }))"
          >
            <v-icon>{{ mdiPen }}</v-icon>
          </td>
          <td v-else></td>
        </tr>
      </template>
    </v-data-table>
    <v-row>
      <v-col cols="3">
        <v-btn v-if="$store.state.auth.userSettings.accCars.write" block color="colorNew" @click="dataNew">
          {{ $t('label.addNew') }}
        </v-btn>
      </v-col>
    </v-row>
    <!----------DIALOGS------------------------------>
    <v-dialog v-model="modalEdit" persistent width="70%">
      <v-card>
        <v-card-title class="headline colorHeadlineStandard" primary-title>
          {{ $tc('label.car') }}
          <v-spacer />
          <v-icon large @click="modalEdit = false">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-form v-model="validForm" ref="carForm">
            <v-row>
              <v-col cols="7">
                <v-text-field
                  :label="$t('label.carPlate')"
                  v-model="car.plate"
                  :rules="[ruleRequired]"
                  :disabled="car.id != 0"
                  maxlength="64"
                  required
                  hide-details
                  dense
                  autofocus
                />
              </v-col>
              <v-col cols="5">
                <v-autocomplete
                  :label="$tc('label.country')"
                  v-model="car.state"
                  :items="listCountries"
                  item-text="czShort"
                  item-value="A3"
                  cache-items
                  append-icon="$dropdown"
                  clearable
                  hide-details
                  dense
                  :prepend-inner-icon="mdiEarth"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field :label="$t('label.carType')" maxlength="64" v-model="car.type" hide-details dense />
              </v-col>
              <v-col cols="6">
                <v-text-field :label="$t('label.carColor')" maxlength="64" v-model="car.color" hide-details dense />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea
                  :label="$t('label.note')"
                  v-model="car.note"
                  maxlength="512"
                  name="input-7-1"
                  no-resize
                  hide-details
                  dense
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn v-if="car.id && $store.state.auth.userSettings.accCars.delete" color="colorDelete" @click="modalDelete = true">
            {{ $t('btn.delete') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="colorSave" @click="dataSave">
            {{ $t('btn.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogConfirmDelete
      :header="$t('label.delete')"
      :body="`${$t('msg.confirmDelete')}: ${car.plate}?`"
      :openDialog.sync="modalDelete"
      v-on:confirm="dataDelete"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import DialogConfirmDelete from '@/components/DialogConfirmDelete.vue';

import { mdiMagnify, mdiPen, mdiClose, mdiEarth } from '@mdi/js';

export default Vue.extend({
  name: 'SettingsCars',
  components: { DialogConfirmDelete },
  data: () => ({
    tableData: [],
    listCountries: [],
    loaderStatus: false,
    modalEdit: false,
    modalDelete: false,
    validForm: false,
    search: '',
    car: {
      id: 0,
      plate: '',
      state: '',
      color: '',
      type: '',
      note: '',
    },
    mdiMagnify: mdiMagnify,
    mdiPen: mdiPen,
    mdiClose: mdiClose,
    mdiEarth: mdiEarth,
  }),
  computed: {
    ruleRequired() {
      return (v: any) => !!v || this.$i18n.t('msg.fieldIsMandatory');
    },
    headers: function () {
      return [
        { text: this.$i18n.t('label.carPlate'), value: 'plate' },
        { text: this.$i18n.tc('label.country'), value: 'state' },
        { text: this.$i18n.t('label.carType'), value: 'type' },
        { text: this.$i18n.t('label.carColor'), value: 'color' },
        { sortable: false, width: '48px' },
      ];
    },
  },
  mounted() {
    this.listCountriesLoad();
    this.$nextTick(function () {
      this.dataLoad();
    });
  },
  methods: {
    dataLoad() {
      this.loaderStatus = true;
      axios
        .get('/api/v1/car')
        .then((response) => {
          this.tableData = response.data;
          this.loaderStatus = false;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
          this.loaderStatus = false;
        });
    },
    dataNew() {
      const defaults: any = this.$options;
      this.car = { ...defaults.data().car };
      this.modalEdit = true;
      this.$nextTick(function () {
        const form: any = this.$refs.carForm;
        form.validate();
      });
    },
    dataSave() {
      const form: any = this.$refs.carForm;
      if (form.validate() !== true) {
        this.$store.commit('globalMessage', this.$i18n.t('msg.formNotValid'));
      } else {
        if (this.car.id === 0) {
          axios
            .post('/api/v1/car', this.car)
            .then((response) => {
              if (response.data.newCarID === -1) {
                this.$store.commit('globalMessage', this.$i18n.t('msg.errCarPlateExists'));
              } else {
                this.dataLoad();
                this.modalEdit = false;
              }
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        } else {
          axios
            .put('/api/v1/car', this.car)
            .then(() => {
              this.dataLoad();
              this.modalEdit = false;
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        }
      }
    },
    dataDelete() {
      axios
        .delete(`/api/v1/car/${this.car.id}`)
        .then(() => {
          this.dataLoad();
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
      this.modalEdit = false;
      this.modalDelete = false;
    },
    listCountriesLoad() {
      axios
        .get(`/api/v1/countries`)
        .then((response) => {
          for (const country of response.data) {
            country.czShort = country.A3 + ' - ' + country.czShort;
          }
          this.listCountries = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
  },
});
</script>
